import DynamicRoute from '@jetshop/core/components/DynamicRoute';
import CustomFont from '@jetshop/core/components/Fonts/CustomFont';
import PaginationProvider from '@jetshop/core/components/Pagination/PaginationProvider';
import { ProductListProvider } from '@jetshop/core/hooks/ProductList/ProductListContext';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import GenericError from '@jetshop/ui/ErrorBoundary/Generic';
import LoadingBar from '@jetshop/ui/Loading/LoadingBar';
import ModalProvider from '@jetshop/ui/Modal/ModalProvider';
import ModalRoot from '@jetshop/ui/Modal/ModalRoot';
import ScrollRestorationHandler from '@jetshop/ui/ScrollRestorationHandler';
import React from 'react';
import Helmet from 'react-helmet-async';
import Loadable from 'react-loadable';
import { Route, Switch } from 'react-router-dom';
import loadFontCss from '../fonts/loadFontCss';
import CategoryPreviewQuery from './CategoryPreviewQuery.gql';
import Container from './Layout/Container';
import Content from './Layout/Content';
import Footer from './Layout/Footer/Footer';
import Header from './Layout/Header/Header';
import LoadingPage from './LoadingPage';
import { Favourites } from './ProductList/Favourites';
import { productListQueries } from './ProductList/productListQueries';
import ProductPreviewQuery from './ProductPreviewQuery.gql';
import routeQuery from './RouteQuery.gql';
import { fonts } from './Theme';
import SandstromsFavicon from '../icos/SandstromsFavicon.ico';
import RagardsFavicon from '../icos/RagardsFavicon.ico';

const NotFound = Loadable({
  loader: () => import('./NotFoundPage'),
  loading: LoadingPage
});

const PreviewRoute = Loadable({
  loader: () => import('@jetshop/core/components/DynamicRoute/PreviewRoute'),
  loading: LoadingPage
});

const NavTreePage = Loadable({
  loader: () => import('./NavigationTree/NavTreePage'),
  loading: LoadingPage
});

const LoadableStartPage = Loadable({
  loader: () => import('./StartPage/StartPage'),
  loading: LoadingPage
});

const LoadableContentPage = Loadable({
  loader: () => import('./ContentPage/ContentPage'),
  loading: LoadingPage
});

const LoadableProductPage = Loadable({
  loader: () => import('./ProductPage/ProductPage'),
  loading: LoadingPage
});

const LoadableCategoryPage = Loadable({
  loader: () => import('./CategoryPage/CategoryPage'),
  loading: LoadingPage
});

const LoadableSearchPage = Loadable({
  loader: () => import('./SearchPage/SearchPage'),
  loading: LoadingPage
});

function Shop() {
  const {
    routes,
    apolloConfig: { shopid }
  } = useShopConfig();
  const isEztrad = shopid === 'eztrad';
  return (
    <GenericError>
      <ModalProvider>
        <Container>
          <LoadingBar />
          <CustomFont primaryFont={fonts.primary} injectCss={loadFontCss} />
          <Helmet
            titleTemplate={isEztrad ? '%s - Rågårds' : '%s - Sandstroms'}
            defaultTitle={isEztrad ? 'Rågårds' : 'Sandstroms'}
          >
            <link
              rel="shortcut icon"
              href={isEztrad ? RagardsFavicon : SandstromsFavicon}
            ></link>
          </Helmet>
          <ProductListProvider queries={productListQueries}>
            <Header />
            <Content>
              <PaginationProvider defaultProductsPerPage={24}>
                <Switch>
                  <Route exact path="/" component={LoadableStartPage} />
                  <Route path="/favourites" component={Favourites} />
                  <Route
                    path={routes.search.path}
                    component={LoadableSearchPage}
                  />
                  <Route path={routes.tree.path} component={NavTreePage} />
                  <Route
                    path="/preview"
                    render={props => (
                      <PreviewRoute
                        productPage={LoadableProductPage}
                        productQuery={ProductPreviewQuery}
                        categoryQuery={CategoryPreviewQuery}
                        categoryPage={LoadableCategoryPage}
                        StartPage={LoadableStartPage}
                        {...props}
                      />
                    )}
                  />
                  <DynamicRoute
                    routeQuery={routeQuery}
                    productPage={LoadableProductPage}
                    categoryPage={LoadableCategoryPage}
                    contentPage={LoadableContentPage}
                    notFoundPage={NotFound}
                    LoadingPage={LoadingPage}
                  />
                </Switch>
              </PaginationProvider>
              <svg
                width="2030"
                height="1623"
                viewBox="0 0 2030 1623"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="graphic-element"
              >
                <path
                  d="M253.6 1328.63C249.87 1323.29 246.55 1317.67 243.7 1311.78C228.24 1279.79 227.89 1242.47 233.13 1207.33C243.52 1137.74 275.24 1066.22 326.29 1016.94C370.26 974.5 432.71 947.96 494.35 954.36C549.09 960.05 602.41 980.36 655.66 993.4C744.02 1015.03 844.57 1025.79 916.85 958.78C973.36 908.78 957.68 817.91 898.84 776.89C842.36 733.94 767.81 735.37 700.93 722.75C645.39 711.67 584.14 708.39 531.66 685.62C498.33 671.16 470.54 634.21 492.59 598.43C511.67 567.47 551.74 563.34 583.42 574.58C623.48 588.79 656.03 617 693.1 636.72C733.87 658.42 775.64 677.99 819.31 693.19C860.1 707.39 902.36 717.97 945.37 722.47C980.17 726.11 1015.57 725.74 1050.06 719.43C1078.85 714.17 1106.96 704.7 1132.48 690.27C1154.93 677.57 1175.23 661.04 1191.73 641.19C1229.13 596.21 1243.05 536.15 1232.81 478.92C1221.93 418.07 1186.88 368.93 1137.38 333.39C1098.69 305.61 1054.63 285.3 1008.29 274.21C866.28 240.22 734.55 311.87 602.02 350.81C537.62 369.73 461.98 379.77 408.27 329.91C396.71 319.18 386.93 305.93 383.05 290.65C377.42 268.48 384.66 245.95 389.59 224.39C394.92 201.06 394.44 178.71 391.71 155.05C389.07 132.2 382.96 109.75 373.2 88.9004C365.02 71.4204 354.31 55.1704 341.55 40.7004C300.48 -5.2696 231.77 -4.7196 176.2 7.6904C118.97 20.4704 75.16 57.9504 44.67 106.86C21.65 143.78 6.04997 185.62 1.68997 229C-9.24003 337.86 57.2 426.56 110.96 514.34C134.55 555.42 198.39 518.47 174.45 477.23C161.3 454.59 147.81 432.34 133.66 410.31C120.19 389.34 106.8 368.2 96.12 345.64C75.64 302.4 67.34 256.59 79.88 210.03C88.89 176.58 103.98 143.15 126.74 116.76C153.04 86.2604 199.86 69.8504 239.47 72.6104C293.27 76.3604 317.52 123.11 319.69 172.93C320.76 197.6 311.45 218.69 308.57 242.47C305.4 268.64 308.09 295.44 317.01 320.29C335.32 371.27 378.15 408.93 428.63 426.72C584.19 482.36 727.72 359.12 879.49 342.42C879.81 342.39 880.12 342.34 880.43 342.28C962 327.19 1064.47 349.5 1120.78 413.41C1146.27 442.34 1162.19 480.26 1161.78 518.82C1161.41 553.58 1146.98 589.89 1120.54 613.07C1095.1 635.37 1061.53 643.29 1029.1 649.23C1003.58 653.71 976.82 651.52 951.3 648.14C920.54 644.06 890.11 637.23 860.39 628.36C827.72 618.61 794.51 606.28 764.28 590.43C736.94 576.09 711.32 558.77 685 542.71C648.05 520.17 608.48 498.9 564.79 494.01C486.36 485.23 416.58 547.04 413.71 625.09C412.38 661.22 425.91 696.81 451.98 722.05C490.67 759.51 552.03 769.51 602.67 779.53C658.25 790.53 714.62 796.89 770.25 807.55C807.32 814.65 882.22 825.69 878.29 879.06C876.18 907.65 843.94 925.3 819.04 931.3C799.77 935.94 779.59 934.35 759.9 932.06C715.51 926.92 671.52 918.44 628.4 906.73C593.87 897.35 559.6 885.88 523.91 881.7C420.87 869.62 315 917.53 247.88 994.81C185.64 1066.47 154.36 1162.82 156.55 1257.71C158.91 1359.83 236.89 1436.42 330.24 1465.82C420.66 1494.31 520.4 1487.94 612.26 1470.17C659.08 1461.11 705.29 1448.48 750.12 1432.23C827.64 1407.89 898.2 1394.62 971.47 1427.33C1060.21 1464.55 1160.97 1572.32 1268.62 1607.82C1375.62 1641.25 1486.16 1614.46 1570.6 1542.13C1657.5 1467.69 1712.26 1358.99 1732.59 1247.47C1739.06 1211.98 1740.72 1176.07 1745.84 1140.52C1750.49 1108.19 1756.33 1076.02 1763.8 1044.22C1778.75 980.6 1800.31 917.86 1833.47 861.3C1843.57 844.07 1854.74 827.44 1867.08 811.73C1877.47 798.48 1889.18 785.99 1903.67 777.18C1923.4 765.18 1948.48 761.84 1969.5 772.3C1988.31 781.66 2012.18 783.92 2024.15 761.94C2034.05 743.2 2027.89 723.35 2010.96 711.64C1950.35 669.72 1870.17 699.13 1824.4 748.51C1743.11 836.53 1709.78 954.21 1687.05 1069.01C1675.64 1126.62 1666.59 1184.69 1654.37 1242.15C1642.11 1299.79 1627.97 1357.43 1595.86 1407.63C1575.14 1440.02 1548.75 1468.81 1518.07 1492C1450.23 1543.27 1353.73 1570.1 1273.12 1531.41C1154.87 1479.18 1068.02 1365.95 937.39 1340.02C831.53 1319.01 728.61 1362.45 628.4 1390.19C547.14 1412.68 464.34 1420.33 381.36 1401.9C353.65 1395.75 326.25 1386.91 301.85 1372.4C283.07 1361.23 266.09 1346.5 253.63 1328.66L253.6 1328.63Z"
                  stroke="#FCD1AF"
                  stroke-miterlimit="10"
                />
              </svg>
            </Content>
            <Footer />
          </ProductListProvider>
          <ModalRoot />
          <ScrollRestorationHandler
            ignoreForRouteTypes={[
              'sortOrderChange',
              'filterChange',
              'paginationChange'
            ]}
          />
        </Container>
      </ModalProvider>
    </GenericError>
  );
}

export default Shop;
